import axios from "axios";
import Qs from "qs";
import dayjs from "dayjs";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import relativeTime from "dayjs/plugin/relativeTime";
import duration from "dayjs/plugin/duration";
import TraceKit from "tracekit";
import StackTrace from 'stacktrace-js';
import { getWebInstrumentations, initializeFaro } from '@grafana/faro-web-sdk';
import { TracingInstrumentation } from '@grafana/faro-web-tracing';
import { ReactIntegration, ReactRouterVersion } from "@grafana/faro-react";
import API from "./API";
import { matchRoutes } from "react-router-dom";
import { isUserMultiTenant } from "./swzutils/UserPermissionsUtil";
import { store } from "./store";
import { changeDoLogout, loggedInUserSelector, selectedTenantSelector } from "./store/slice/auth";
export const setupAxios = () => {
    const apiBaseURL = API.origin();
    if (apiBaseURL) {
        if (console && console.log) {
            console.log("Setting API baseURL to ", apiBaseURL);
        }
        axios.defaults.baseURL = apiBaseURL;
        axios.defaults.withCredentials = true;
    }
    axios.defaults.paramsSerializer = {
        serialize: function (params) {
            return Qs.stringify(params, { arrayFormat: 'repeat' });
        }
    };
    axios.interceptors.request.use((config) => {
        const state = store.getState();
        const loggedInUser = loggedInUserSelector(state);
        const tenant = selectedTenantSelector(state);
        if (isUserMultiTenant(loggedInUser) && tenant?.id) {
            config.headers['TenantId'] = tenant?.id;
        }
        return config;
    });
    axios.interceptors.response.use(function (response) {
        // Nothing to do here.
        return response;
    }, function (error) {
        // Logout when unauthorised response status is received and user is logged in
        if (error.response && error.response.status === 401) {
            const loggedInUser = loggedInUserSelector(store.getState());
            if (loggedInUser) {
                store.dispatch(changeDoLogout(true));
            }
        }
        return Promise.reject(error);
    });
};
export const setupDayJS = () => {
    dayjs.extend(isSameOrAfter);
    dayjs.extend(relativeTime);
    dayjs.extend(duration);
};
export const setupStackTraceJS = () => {
    window.onerror = function (event, source, lineno, colno, error) {
        // callback is called with an Array[StackFrame]
        if (!error) {
            return;
        }
        StackTrace.fromError(error)
            .then((stackFrames) => {
            const request = {
                stack: stackFrames,
                event,
                source,
                lineno,
                colno
            };
            axios.post('/admin/v1/errors', request);
        });
    };
};
// TraceKit adds the surrounding lines of source code to each stack frame. The production bundle is minified into
// very long lines (hundreds of KB) so without trimming a single error report can be many MB.
const MAX_CONTEXT_LINE_LENGTH = 500;
const trimContextLine = (line, column) => {
    if (line.length <= MAX_CONTEXT_LINE_LENGTH) {
        return line;
    }
    // Keep the code around the error column when known, otherwise the start of the line
    const start = column ? Math.max(0, column - 1 - MAX_CONTEXT_LINE_LENGTH / 2) : 0;
    return line.substring(start, start + MAX_CONTEXT_LINE_LENGTH);
};
const trimStackContext = (errorReport) => {
    errorReport.stack?.forEach(frame => {
        if (frame.context) {
            // Index of the frame's line within the context, as calculated by TraceKit's gatherContext().
            // linesOfContext is a number, it is incorrectly declared as a boolean in tracekit.d.ts
            const linesBefore = Math.floor(TraceKit.linesOfContext / 2);
            const frameLineIndex = Math.min(frame.line - 1, linesBefore);
            frame.context = frame.context.map((line, index) => trimContextLine(line, index === frameLineIndex ? frame.column : null));
        }
    });
};
export const setupTraceKit = () => {
    TraceKit.collectWindowErrors = true;
    let errorRequestInProgress = false;
    TraceKit.report.subscribe(errorReport => {
        if (errorRequestInProgress) {
            return; // prevent infinite recursion
        }
        if ("qa-admin.subtrux-test.net" === window.location.host) {
            console.log("errorReport", errorReport);
        }
        // @ts-ignore
        if (errorReport.mode === "callers" && errorReport.incomplete === true && !errorReport.message && !errorReport.name) {
            return; // Suppress incomplete error reports with no error message and no name since there is nothing we can do with it.
        }
        trimStackContext(errorReport);
        errorRequestInProgress = true;
        axios.post('/admin/v1/errors', errorReport).then(( /*response*/) => {
            errorRequestInProgress = false;
        }).catch(function ( /*error*/) {
            errorRequestInProgress = false;
        });
    });
};
export const setupGrafanaFaro = () => {
    const faro = initializeFaro({
        url: 'https://faro-collector-prod-au-southeast-1.grafana.net/collect/16e586cc1b7b884c5db4cc9ec0ed8ac5',
        app: {
            name: 'SubTrux Admin',
            // @ts-ignore
            version: window.SUBTRUX_BUILD,
            // @ts-ignore
            environment: window.SUBTRUX_ENV,
            // release?: string;
        },
        instrumentations: [
            // Mandatory, overwriting the instrumentations array would cause the default instrumentations to be omitted
            ...getWebInstrumentations(),
            // Initialization of the tracing package.
            // This packages is optional because it increases the bundle size noticeably. Only add it if you want tracing data.
            new TracingInstrumentation(),
            new ReactIntegration({
                router: {
                    version: ReactRouterVersion.V6_data_router,
                    dependencies: {
                        matchRoutes,
                    },
                    // version: ReactRouterVersion.V6,
                    // dependencies: {
                    //     createRoutesFromChildren,
                    //     matchRoutes,
                    //     Routes,
                    //     useLocation,
                    //     useNavigationType,
                    // },
                },
            })
        ],
    });
    return faro;
};
